import { createPortal } from 'react-dom';
import { useHeaderActionsTarget } from '@/layouts/usePageTitle';
import HomeTagFilter from '@/components/dashboard/HomeTagFilter';
import { useEffect, useState } from 'react';
import { fetchCardData, fetchPowerChart, type CardData, type PowerChartData } from '@/api/dashboard';
import { fetchPowerPlantTagFilter, savePowerPlantTagFilter } from '@/api/powerplants';
import { fetchTagCategories, fetchTags, type TagItem } from '@/api/tags';
import { fetchInverterHeatmap, type InverterHeatmapRow } from '@/api/inverters';
import StatCards, { DashboardStatusCards } from '@/components/dashboard/StatCards';
import PowerBarChart from '@/components/dashboard/PowerBarChart';
import InverterHeatmapPanel from '@/components/inverters/InverterHeatmapPanel';
import { useI18n } from '@/i18n';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useErrorToast, LoadRetry } from '@/lib/notify';

type FilterTag = Pick<TagItem, 'id' | 'label' | 'color'>;

function PageSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      {[0, 1].map(row => <div key={row} className="grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
        {Array.from({length:5},(_,i)=><div key={i} className="h-[76px] rounded-xl bg-gray-200"/>)}
      </div>)}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-4">
        <div className="h-[290px] rounded-2xl bg-gray-200 xl:col-span-3" />
        <div className="h-[290px] rounded-2xl bg-gray-200 xl:col-span-1" />
      </div>
    </div>
  );
}

export default function HomePage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const headerActionsTarget = useHeaderActionsTarget();
  const [cards, setCards] = useState<CardData | null>(null);
  const [chart, setChart] = useState<PowerChartData | null>(null);
  const [heatmapRows, setHeatmapRows] = useState<InverterHeatmapRow[]>([]);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [topPanelOpen, setTopPanelOpen] = useState(true);
  const [availableTags, setAvailableTags] = useState<FilterTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  useErrorToast(error);

  useEffect(() => {
    let cancelled = false;

    Promise.all([fetchTagCategories(), fetchPowerPlantTagFilter()])
      .then(async ([categories, savedTagIds]) => {
        const category = categories.find((item) => item.code === 'power_plant');
        const tags = category ? await fetchTags(category.id) : [];
        const normalizedTags: FilterTag[] = tags
          .filter((tag) => tag.is_active === 1)
          .map((tag) => ({ id: Number(tag.id), label: tag.label, color: tag.color }))
          .sort((a, b) => a.label.localeCompare(b.label, 'tr'));

        if (cancelled) return;
        setAvailableTags(normalizedTags);
        setSelectedTagIds(savedTagIds.map((id) => Number(id)).filter((id) => Number.isInteger(id)));
      })
      .catch(() => {
        if (!cancelled) setError(t('home.loadError'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (loading) return;
    setHeatmapLoading(true);
    Promise.all([fetchCardData(selectedTagIds), fetchPowerChart(selectedTagIds), fetchInverterHeatmap(undefined, selectedTagIds)])
      .then(([cardData, powerChart, heatmap]) => {
        setCards(cardData);
        setChart(powerChart);
        setHeatmapRows(heatmap.rows || []);
      })
      .catch(() => {
        setHeatmapRows([]);
      })
      .finally(() => {
        setHeatmapLoading(false);
      });
    void savePowerPlantTagFilter(selectedTagIds);
  }, [loading, selectedTagIds]);


  const instantPowerKw=heatmapRows.reduce((sum,row)=>{const value=Number(row.power_kw);return Number.isFinite(value)?sum+value:sum;},0);
  const inverterWarningCount=heatmapRows.filter(row=>row.status===1 && row.performance_ratio!=null && Number.isFinite(Number(row.performance_ratio)) && Number(row.performance_ratio)<60).length;

  if (loading) return <PageSkeleton />;
  if (error) return <LoadRetry />;
  if (!cards || !chart) return <PageSkeleton />;

  return (
    <div className="flex min-h-full flex-col gap-2 pb-6">
      {headerActionsTarget && createPortal(<HomeTagFilter tags={availableTags} selectedIds={selectedTagIds} onChange={setSelectedTagIds}/>,headerActionsTarget)}

      <div className="rounded-2xl border border-slate-100 bg-white p-2.5 shadow-sm">
        <div className="mb-2 flex items-center justify-between px-1">
          <div className="text-xs font-bold text-slate-900">Metrikler</div>
          <button
            type="button"
            onClick={() => setTopPanelOpen((prev) => !prev)}
            className="inline-flex h-6 w-6 items-center justify-center rounded-lg text-slate-500 transition hover:bg-slate-50"
            title={topPanelOpen ? 'Metrikleri gizle' : 'Metrikleri goster'}
          >
            {topPanelOpen ? <ChevronUp size={15} /> : <ChevronDown size={15} />}
          </button>
        </div>

        {topPanelOpen && <StatCards data={cards} loading={heatmapLoading} instantPowerKw={heatmapLoading?null:instantPowerKw} />}
      </div>

      <DashboardStatusCards data={cards} loading={heatmapLoading} inverterWarningCount={heatmapLoading?undefined:inverterWarningCount} />

      <PowerBarChart data={chart} tagIds={selectedTagIds} compact showHourly={false} className="shrink-0 rounded-xl border-slate-200" titleClassName="text-lg font-bold leading-7 tracking-normal" />

      <InverterHeatmapPanel
        showSummary={false}
        rows={heatmapRows}
        loading={heatmapLoading}
        onInverterClick={(inverterId) => navigate(`/dashboard/inverters/${inverterId}`)}
      />

    </div>
  );
}
